/**
 * The keno board as data: the 80-cell grid's geometry, what each cell shows
 * at every beat of a draw, and the reveal count the TV animates to the settle
 * deadline. Pure, so the phone, the TV and the tests all read the same board.
 */
import { KENO_DRAW_SIZE, KENO_POOL, kenoPaytable, type KenoPaytableName } from '@tv-casino/shared';
import { playerColor } from '../lib/playerColors.js';

/** The board prints ten columns of eight — the shape every card uses. */
export const KENO_BOARD_COLUMNS = 10;
export const KENO_BOARD_ROWS = KENO_POOL / KENO_BOARD_COLUMNS;

/**
 * Fixed palette by ticket order (A–J), the way a 4-card keno card colours
 * each wager. Index wraps past ten so a maxed slate still reads distinctly.
 */
export const KENO_TICKET_COLORS = [
  '#ffd666', // gold
  '#4fb8ff', // sky
  '#ff7ab6', // pink
  '#7ed957', // lime
  '#b18cff', // violet
  '#40d6c8', // teal
  '#ffb347', // amber
  '#f45fc0', // magenta
  '#ccd6dd', // silver
  '#ff6b6b', // coral
] as const;

/**
 * The colour ticket N wears on every board and rail. N is the ticket's table
 * order either way — the toggle only swaps the palette: felt mode wraps the
 * seat hues chips use on the other felts, lounge mode the ten-hue card.
 */
export const ticketColor = (index: number, classicColors = false): string => {
  if (!classicColors) return playerColor(index);
  const n = KENO_TICKET_COLORS.length;
  return KENO_TICKET_COLORS[((index % n) + n) % n]!;
};

/**
 * Where one seat's colours start: every ticket on a lower seat comes first.
 * The TV board paints the whole table at once, so numbering tickets in seat
 * order is the only way a phone's swatch and its ring on the TV can agree.
 */
export const kenoColorStart = (
  seats: readonly { index: number; tickets: readonly unknown[] }[],
  seatIndex: number,
): number =>
  seats.reduce((n, seat) => (seat.index < seatIndex ? n + seat.tickets.length : n), 0);

/**
 * Palette index for one ticket: its table order — every ticket on a lower
 * seat first — so each ticket keeps one hue in either palette.
 */
export const kenoTicketColorIndex = (
  seats: readonly { index: number; tickets: readonly unknown[] }[],
  seatIndex: number,
  ticketIndex: number,
): number => kenoColorStart(seats, seatIndex) + ticketIndex;

/** Which row (1-based) a spot sits in, reading left-to-right, top-down. */
export const boardRow = (spot: number): number => Math.floor((spot - 1) / KENO_BOARD_COLUMNS) + 1;

/** Which column (1-based) a spot sits in within its row. */
export const boardCol = (spot: number): number => ((spot - 1) % KENO_BOARD_COLUMNS) + 1;

export interface KenoCellVM {
  spot: number;
  row: number;
  col: number;
  /** The draw picked this number. */
  drawn: boolean;
  /** One of the viewer's tickets picked this number. */
  picked: boolean;
  /** Both of the above: the happy intersection. */
  caught: boolean;
  /**
   * Which tickets' colour indexes mark this spot. Empty when the board is in
   * merged-gold mode; one or more when colour-coding the slate.
   */
  ticketIndexes: number[];
  /** On the ticket being written right now, not yet added. */
  draft: boolean;
}

/** A ticket as the board paints it. `colorIndex` defaults to its list position. */
export interface KenoBoardTicket {
  spots: readonly number[];
  colorIndex?: number;
}

export interface KenoBoardInput {
  /** The twenty numbers in draw order, or null before the draw. */
  draw: readonly number[] | null;
  /** The numbers the viewer has a stake in — every ticket's spots merged. */
  picked?: readonly number[];
  /**
   * When given, each ticket paints its own colour on the board. Overlaps list
   * every ticket index that claims the spot. Takes precedence over `picked`.
   */
  tickets?: readonly KenoBoardTicket[];
  /** The spots tapped onto the ticket being written, drawn over the slate. */
  draft?: readonly number[];
  /**
   * How many of the twenty balls are out of the machine. The settled board
   * passes the full draw; a board waiting on the animation passes fewer.
   */
  reveal?: number;
}

/**
 * Per-spot colour indexes for the colour-coded board. Oldest ticket is 0
 * unless a ticket names its own; a spot on three tickets lists `[0, 2, 4]`.
 */
export function ticketMarks(tickets: readonly KenoBoardTicket[]): Map<number, number[]> {
  const marks = new Map<number, number[]>();
  tickets.forEach((ticket, index) => {
    const color = ticket.colorIndex ?? index;
    for (const spot of ticket.spots) {
      const existing = marks.get(spot);
      if (existing) existing.push(color);
      else marks.set(spot, [color]);
    }
  });
  return marks;
}

/** All eighty cells, in board order, for whatever the draw has shown so far. */
export function kenoBoardCells({
  draw,
  picked = [],
  tickets,
  draft = [],
  reveal = draw ? KENO_DRAW_SIZE : 0,
}: KenoBoardInput): KenoCellVM[] {
  const shown = new Set(draw ? draw.slice(0, Math.max(0, Math.min(reveal, draw.length))) : []);
  const bySpot = tickets ? ticketMarks(tickets) : null;
  const mine = bySpot ? null : new Set(picked);
  const drafted = new Set(draft);
  return Array.from({ length: KENO_POOL }, (_, i) => {
    const spot = i + 1;
    const drawn = shown.has(spot);
    const ticketIndexes = bySpot?.get(spot) ?? [];
    const isPicked = mine ? mine.has(spot) : ticketIndexes.length > 0;
    return {
      spot,
      row: boardRow(spot),
      col: boardCol(spot),
      drawn,
      picked: isPicked,
      caught: drawn && isPicked,
      ticketIndexes,
      draft: drafted.has(spot),
    };
  });
}

/**
 * How long the board's reveal runs. The server settles `kenoSettleMs` (4.5s)
 * after the draw lands; the reveal runs just under that, so the last ball sits
 * lit for a beat before the chips move.
 */
export const KENO_REVEAL_MS = 4_200;

/**
 * How many balls are out at `now`, given when the reveal started and when the
 * server settles. Linear, clamped to the draw size: the TV ticks this on a
 * frame and the chips move exactly when it reaches twenty. Under reduced
 * motion the caller passes `endsAt` as `now` and the whole draw lands at once.
 */
export function revealCount(
  startedAt: number,
  endsAt: number,
  now: number,
  size = KENO_DRAW_SIZE,
): number {
  if (endsAt <= startedAt) return size;
  const progress = (now - startedAt) / (endsAt - startedAt);
  return Math.max(0, Math.min(size, Math.ceil(progress * size)));
}

/**
 * Whether a board should be running the reveal at all: a draw is out, it has
 * not settled, the server named a deadline, and the viewer has not asked for
 * reduced motion. Anything else shows the whole draw at once.
 */
export const kenoRevealAnimates = (
  draw: readonly number[] | null,
  settled: boolean,
  endsAt: number | null,
  reduced: boolean,
): boolean => draw !== null && !settled && !reduced && endsAt !== null;

/**
 * How many balls a board shows at `now`: nothing before a draw, the whole
 * draw when the reveal is not running, otherwise the count on the clock that
 * ends at the server's settle deadline.
 */
export function kenoRevealAt(
  draw: readonly number[] | null,
  settled: boolean,
  endsAt: number | null,
  reduced: boolean,
  now: number,
): number {
  if (!draw) return 0;
  if (!kenoRevealAnimates(draw, settled, endsAt, reduced) || endsAt === null) return draw.length;
  return revealCount(endsAt - KENO_REVEAL_MS, endsAt, now, draw.length);
}

/**
 * When the next ball lands, given `count` are already out: one millisecond
 * past its share of the reveal, so a timer set for it wakes on the far side
 * of the `ceil` in `revealCount`. The hook sleeps until then instead of
 * polling, since nothing on the board changes between balls.
 */
export const nextBallAt = (
  startedAt: number,
  count: number,
  size: number,
  span = KENO_REVEAL_MS,
): number => startedAt + (count / size) * span + 1;

/** The spots of one viewer's tickets, merged — what the board marks as picked. */
export const pickedSpots = (
  tickets: readonly { spots: readonly number[] }[],
): number[] => [...new Set(tickets.flatMap((ticket) => [...ticket.spots]))];

/** One printed line of a keno pay card: catch this many, get this back. */
export interface KenoPayRow {
  catches: number;
  /** Total return per chip staked, stake included. */
  multiplier: number;
  /** Whole chips this ticket returns at its stake. */
  pays: number;
}

/**
 * The pay card for a ticket being written, the way a keno lounge prints it
 * per spot count: best catch first, only the lines that pay, priced at the
 * armed stake. A 10-spot's catch-0 consolation lists last. Empty for a ticket
 * with no spots or more than the slate prints.
 */
export function kenoPayRows(paytable: KenoPaytableName, spots: number, stake: number): KenoPayRow[] {
  const row = kenoPaytable(paytable)[spots] ?? [];
  return row
    .map((multiplier, catches) => ({ catches, multiplier, pays: multiplier * stake }))
    .filter((line) => line.multiplier > 0)
    .reverse();
}

/** The stake rail the other tables use; the table min leads when it is off it. */
const KENO_QUICK_STAKES = [5, 10, 25, 100];

/**
 * The phone's stake rail for a keno ticket: the table min, then the house
 * rail above it, never past the max. Web and native print the same pills.
 */
export function kenoQuickStakes(minBet: number, maxBet: number): number[] {
  const rail = KENO_QUICK_STAKES.filter((stake) => stake >= minBet);
  const list = rail[0] === minBet ? rail : [minBet, ...rail];
  return list.filter((stake) => stake <= maxBet);
}

/**
 * How a settled ticket reads on the phone: "3 of 5 · +65" or "dry · −5".
 * Payout includes the stake, so a push is `0` and a win is positive.
 */
export function ticketResultLabel(amount: number, payout: number | null): string | null {
  if (payout == null) return null;
  const net = payout - amount;
  if (net > 0) return `+${net.toLocaleString()}`;
  if (net === 0) return 'push';
  return `−${amount.toLocaleString()}`;
}

/**
 * A settled ticket's whole verdict in one segment: "caught 3 · push",
 * "dry · −5". The signed half stays `ticketResultLabel`'s; "dry" is reserved
 * for a ticket that caught nothing and paid nothing — a zero-catch
 * consolation or a push still says "caught 0".
 */
export function ticketResultText(
  catches: readonly number[] | null,
  amount: number,
  payout: number | null,
): string | null {
  const net = ticketResultLabel(amount, payout);
  if (catches == null || net == null) return null;
  const caught =
    catches.length > 0
      ? `caught ${catches.length}`
      : net === 'push' || net.startsWith('+')
        ? 'caught 0'
        : 'dry';
  return `${caught} · ${net}`;
}

// --- the board's dress ------------------------------------------------------

/** A ball's two stops and its edge — a flat renderer takes `top`. */
export interface KenoBallFace {
  top: string;
  bottom: string;
  edge: string;
}

/**
 * Everything the board paints, resolved. The web keeps these as custom
 * properties on the board element (`KenoBoard.module.css`); a native renderer
 * has no cascade, so the same two palettes resolve here and both shells read
 * the record.
 */
export interface KenoDress {
  /** The glass behind the balls, and the rail around it. */
  ground: string;
  edge: string;
  /** An uncalled ball. */
  ball: KenoBallFace;
  /** A spot tapped onto the ticket being written. */
  draft: KenoBallFace;
  /** A ball the draw has called. */
  drawn: KenoBallFace & { ink: string };
}

/** The felt the board is dressed in — the tokens' three cloth shades. */
export interface KenoFeltShades {
  deep: string;
  base: string;
  light: string;
}

/**
 * The board's two palettes. Default: the table's own felt, a called ball a
 * cream keno ball with ink numerals. `classicColors`: the video-keno cabinet —
 * deep blue lacquer balls on black glass, called balls flipping hot
 * orange-red. The values are the ones the stylesheet ships, so a restyle is
 * one diff read by three renderers.
 */
export function kenoDress(classicColors: boolean, felt: KenoFeltShades): KenoDress {
  if (classicColors) {
    return {
      ground: '#050b1e',
      edge: 'rgba(255, 214, 102, 0.3)',
      ball: { top: '#1c55d4', bottom: '#123a9e', edge: '#0a2260' },
      draft: { top: '#2f6cf0', bottom: '#1a47b8', edge: '#0a2260' },
      drawn: {
        top: '#ff7a1a',
        bottom: '#e8380d',
        edge: 'rgba(255, 122, 26, 0.85)',
        ink: '#ffffff',
      },
    };
  }
  return {
    ground: felt.deep,
    edge: 'rgba(232, 195, 122, 0.3)',
    ball: { top: felt.light, bottom: felt.base, edge: felt.deep },
    draft: { top: felt.light, bottom: felt.light, edge: felt.deep },
    drawn: {
      top: '#f5efe2',
      bottom: '#d8ccb2',
      edge: 'rgba(232, 195, 122, 0.9)',
      ink: '#08150f',
    },
  };
}
